import type { ReactNode } from "react";
import { Panel, Separator as PanelResizeHandle } from "react-resizable-panels";

import {
  type DockedPanelId,
  FILLER_MIN_HEIGHT,
  PANEL_COLLAPSED_HEIGHT,
  PANEL_DEFAULT_HEIGHT,
  PANEL_MIN_HEIGHT,
  panelLayoutId,
  type usePanelStack,
} from "./usePanelStack";

/**
 * Narrowest the note may become when the rail sits beside it. This is what caps
 * the rail's width — the rail has no maximum of its own, so a drag can widen it
 * until the note reaches this floor, as the design allows.
 */
export const NOTE_MIN_WIDTH = 240;

/**
 * Narrowest the note may become when a panel is stacked above or below it. The
 * note is its group's filler, so this doubles as the cap on how tall a stacked
 * panel can grow.
 */
export const NOTE_MIN_HEIGHT = FILLER_MIN_HEIGHT;

export const RAIL_DEFAULT_WIDTH = 320;
export const RAIL_MIN_WIDTH = 220;
export const RAIL_HANDLE_WIDTH = 14;

/**
 * Width at which the note and the rail fit side by side, measured on the
 * note+rail area rather than the viewport. Derived rather than pinned to a
 * named breakpoint: side by side is worth it once the rail fits at its default
 * width without pushing the note under its own minimum. Narrower than this
 * every panel stacks in the note column, which is how the Chrome extension
 * panel renders.
 */
export const RAIL_SIDE_BY_SIDE_MIN_WIDTH =
  NOTE_MIN_WIDTH + RAIL_DEFAULT_WIDTH + RAIL_HANDLE_WIDTH;

/** Absorbs the rail's leftover height so docked panels stay stacked at the top. */
export const RAIL_FILLER_ID = "rail-filler";
export const NOTE_PANEL_ID = "note-editor";

export const PANEL_GROUP_CLASS =
  "h-full min-h-0 [&_[data-panel]]:outline-none [&_[data-separator]]:outline-none";

export const RESIZE_TARGET_MINIMUM_SIZE = { coarse: 28, fine: 12 };

export function resizeHandleClass(
  enabled: boolean,
  orientation: "x" | "y",
): string {
  if (!enabled) {
    return orientation === "y"
      ? "group/resize flex h-2 w-full items-center justify-center bg-background"
      : "group/resize flex w-2 items-stretch justify-center bg-background";
  }

  return orientation === "y"
    ? "group/resize flex h-3 w-full cursor-ns-resize items-center justify-center bg-background outline-none transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
    : "group/resize flex w-3 cursor-ew-resize items-stretch justify-center bg-background outline-none transition-colors hover:bg-muted/50 focus-visible:bg-muted/50";
}

export function ResizeGrip({
  orientation,
  visible,
}: {
  orientation: "x" | "y";
  /** Collapsed handles stay faintly visible so the stack seams remain findable. */
  visible?: boolean;
}) {
  const base =
    orientation === "y"
      ? "h-1 w-16 rounded-full bg-muted-foreground/30 transition-opacity group-hover/resize:opacity-100"
      : "my-auto h-16 w-1 rounded-full bg-muted-foreground/30 transition-opacity group-hover/resize:opacity-100";
  return <span className={`${base} ${visible ? "opacity-60" : "opacity-0"}`} />;
}

/**
 * A docked panel plus the handle that resizes it. `handleSide` says which side
 * of the panel the handle sits on, which depends on whether the filler panel
 * is below the panel (the rail, and the agenda above the note) or above it
 * (the codes below the note).
 */
export function renderStackMember({
  id,
  stack,
  handleSide,
  isOpen,
  children,
}: {
  id: DockedPanelId;
  stack: ReturnType<typeof usePanelStack>;
  handleSide: "before" | "after";
  isOpen: boolean;
  children: ReactNode;
}): ReactNode[] {
  const rememberedHeight = stack.heightsRef.current[id] ?? PANEL_DEFAULT_HEIGHT;
  const panel = (
    <Panel
      key={id}
      id={panelLayoutId(id)}
      panelRef={stack.panelRefs[id]}
      minSize={isOpen ? PANEL_MIN_HEIGHT : PANEL_COLLAPSED_HEIGHT}
      defaultSize={
        isOpen
          ? Math.max(PANEL_MIN_HEIGHT, rememberedHeight)
          : PANEL_COLLAPSED_HEIGHT
      }
      groupResizeBehavior="preserve-pixel-size"
      onResize={(size) => stack.rememberHeight(id, size.inPixels)}
    >
      {children}
    </Panel>
  );
  const handle = (
    <PanelResizeHandle
      key={`${id}-handle`}
      id={`${panelLayoutId(id)}-handle`}
      disabled={!isOpen}
      className={resizeHandleClass(isOpen, "y")}
      aria-label={`Resize ${id} panel`}
    >
      <ResizeGrip orientation="y" visible={!isOpen} />
    </PanelResizeHandle>
  );

  return handleSide === "after" ? [panel, handle] : [handle, panel];
}
